import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';
import { SavedSearchService } from 'src/app/services/saved-search/saved-search.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-saved-search',
  templateUrl: './saved-search.component.html',
  styleUrls: ['./saved-search.component.scss']
})
export class SavedSearchComponent implements OnInit, OnDestroy {
  @Input() gridApi: any;
  @Input() viewFilterType: string = '';
  @Input() module: string = '';
  @Input() pinnedColumns: string[] = ['action', 'Action', '0'];

  @Output() onClearFilters = new EventEmitter<void>();

  public showViewFilterSection: boolean = false;
  public showAddViewFilterButton = false;
  public showActionViewSearchButton = false;
  public showClearFilter = false;
  public showViewFilterSelectInput = true;
  public showViewFilterRemoveButton = false;

  public selectedView: any = null;
  public saveViewArray: any[] = [];
  public viewFilterInput: string = '';

  public skipEventHandler: boolean = false;
  public isModified: boolean = false;
  public isSaving: boolean = false;
  public isAutoSavingDefault: boolean = false;

  private saveSubject = new Subject<void>();
  private saveSubscription!: Subscription;

  constructor(
    private savedSearchService: SavedSearchService,
    private _modalService: NgbModal
  ) { }

  ngOnInit(): void {
    if (this.gridApi) {
      this.fetchViewFilterList(true);
    }
    this.saveSubscription = this.saveSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.executeSaveColumnState();
    });
  }

  ngOnDestroy(): void {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }

  // To be called from parent when grid is ready
  refresh(gridApi: any) {
    this.gridApi = gridApi;
    this.fetchViewFilterList(true);
  }

  async fetchViewFilterList(isActiveChange: boolean) {
    if (!this.viewFilterType) return;

    let lists: any = await this.savedSearchService.fetchViewFilterList(this.viewFilterType);
    let viewL: any[] = lists.data || [];

    if (viewL.length > 0) {
      this.showViewFilterSection = true;
      this.saveViewArray = viewL;

      let currentViewFilter: any;
      let activeViewId: any;

      if (isActiveChange) {
        const stateRes = await this.savedSearchService.fetchColumnState({
          type: this.viewFilterType,
          module: this.module
        });
        currentViewFilter = stateRes.data;
        activeViewId = this.saveViewArray.find(item => item.status === true)?._id;
      } else {
        const activeView = this.saveViewArray.find(item => item.status === true);
        currentViewFilter = activeView;
        activeViewId = activeView?._id;
      }

      if (activeViewId) {
        this.selectedView = activeViewId;
        this.saveViewArray.forEach(v => v.status = (v._id === activeViewId));
        this.showViewFilterRemoveButton = true;
        this.showAddViewFilterButton = false;
        this.showViewFilterSelectInput = true;

        if (this.gridApi) {
          this.skipEventHandler = true;
          this.gridApi.applyColumnState({ state: currentViewFilter.header_filter, applyOrder: true });
          this.gridApi.setFilterModel(currentViewFilter.column_filter);
          this.skipEventHandler = false;
          this.isModified = false;
        }
      } else {
        this.selectedView = null;
        this.saveViewArray.forEach(v => v.status = false);
        this.showAddViewFilterButton = false;
        this.showViewFilterRemoveButton = false;
        this.showViewFilterSelectInput = true;
      }
    } else {
      this.saveViewArray = [];
      if (this.gridApi && !this.isAutoSavingDefault) {
        this.isAutoSavingDefault = true;
        this.autoSaveDefaultBasic();
      } else {
        if (this.gridApi) {
          this.gridApi.resetColumnState();
          this.gridApi.setFilterModel(null);
        }
        this.showViewFilterSection = true;
        this.showViewFilterSelectInput = false;
        this.showViewFilterRemoveButton = false;
        this.viewFilterInput = '';
        this.showAddViewFilterButton = true;
      }
    }
    this.skipEventHandler = false;
    this.showClearFilterValidation();
  }

  async autoSaveDefaultBasic() {
    if (!this.gridApi) return;
    
    // Slight delay to ensure AG Grid is fully initialized with its native defaults
    setTimeout(async () => {
      try {
        const payload = {
          name: "Default Basic",
          column_filter: this.gridApi.getFilterModel(),
          header_filter: this.gridApi.getColumnState(),
          type: this.viewFilterType,
        };

        const res = await this.savedSearchService.saveViewFilter(payload);
        if (res.status === 'success') {
          await this.updateColumnState(this.gridApi.getFilterModel(), this.gridApi.getColumnState());
          await this.fetchViewFilterList(true); // Refetch so the UI shows the new view
        }
      } catch (e) {
        console.error("Auto-save Default Basic failed", e);
      } finally {
        this.isAutoSavingDefault = false; // Reset just in case it's needed again
      }
    }, 500);
  }

  saveColumnState() {
    this.saveSubject.next();
  }

  async executeSaveColumnState() {
    if (!this.gridApi || this.skipEventHandler) return;
    const filterModel = this.gridApi.getFilterModel();
    const columnState = this.gridApi.getColumnState();
    
    // Always ensure sticky columns are saved correctly to avoid corrupting the database state
    if (columnState) {
      columnState.forEach((col: any) => {
        if (this.pinnedColumns.includes(col.colId)) {
           col.hide = false;
           col.pinned = 'left';
        }
      });
    }

    // Sync global column state
    this.updateColumnState(filterModel, columnState);

    // If a view is selected, update it as well
    if (this.selectedView && this.saveViewArray.length > 0) {
      const currentView = this.saveViewArray.find(v => v._id === this.selectedView);
      if (currentView) {
        this.isSaving = true;
        this.isModified = true;
        try {
          const payload = {
            name: currentView.name,
            status: true,
            type: this.viewFilterType,
            column_filter: filterModel,
            header_filter: columnState
          };
          // Synchronize locally first so UI is ready
          currentView.column_filter = filterModel;
          currentView.header_filter = columnState;
          
          await this.savedSearchService.updateViewFilter(this.selectedView, payload);
          this.isModified = false;
        } catch (error) {
          console.error("Auto-save failed", error);
        } finally {
          this.isSaving = false;
        }
      }
    } else {
      this.isModified = true;
    }
  }

  async updateColumnState(column_filter: any, header_filter: any) {
    const payload = {
      column_filter: column_filter,
      header_filter: header_filter,
      module: this.module,
      type: this.viewFilterType,
    };
    try {
      await this.savedSearchService.updateColumnState(payload);
    } catch (e) {}
  }

  showClearFilterValidation() {
    if (!this.gridApi) return;
    
    const filterModel = this.gridApi.getFilterModel();
    const hasFilter = filterModel && Object.keys(filterModel).length > 0;
    
    const colState = this.gridApi.getColumnState();
    const hasSort = colState && colState.some((col: any) => col.sort !== null && col.sort !== undefined);

    this.showClearFilter = !!(hasFilter || hasSort);
  }

  addViewFilter() {
    this.viewFilterInput = '';
    this.showAddViewFilterButton = true;
    this.showViewFilterSelectInput = false;
    this.showViewFilterRemoveButton = false;
  }

  hideAddViewFilter() {
    if (this.saveViewArray.length > 0) {
      this.showAddViewFilterButton = false;
      this.showViewFilterSelectInput = true;
      this.showViewFilterRemoveButton = this.selectedView ? true : false;
    } else {
      this.showViewFilterSection = false;
    }
  }

  async selectViewFilterEvent() {
    if (this.selectedView) {
      this.saveViewArray.forEach(v => v.status = false);
      const currentViewFilter = this.saveViewArray.find(
        (item) => item._id === this.selectedView
      );

      if (currentViewFilter) {
        currentViewFilter.status = true;
      }

      if (currentViewFilter?.header_filter) {
        currentViewFilter.header_filter.forEach((col: any) => {
          if (this.pinnedColumns.includes(col.colId)) {
             col.hide = false;
             col.pinned = 'left';
          }
        });
      }

      this.skipEventHandler = true;
      if (this.gridApi) {
        this.gridApi.applyColumnState({
          state: currentViewFilter?.header_filter,
          applyOrder: true,
        });
        this.gridApi.setFilterModel(currentViewFilter?.column_filter);
      }
      this.skipEventHandler = false;
      this.isModified = false;

      // Sync status and column state in the background
      const payload = {
        name: currentViewFilter?.name,
        status: true,
        type: this.viewFilterType,
      };
      this.savedSearchService.updateViewFilter(currentViewFilter?._id, payload);
      this.updateColumnState(currentViewFilter?.column_filter, currentViewFilter?.header_filter);

      this.showViewFilterRemoveButton = true;
    } else {
      this.showViewFilterRemoveButton = false;
      this.clearFilter();
      this.skipEventHandler = true;
    }
    this.showClearFilterValidation();
    this.showActionViewSearchButton = false;
  }

  async reApplyCurrentView() {
    if (!this.selectedView || !this.gridApi) return;
    const currentViewFilter = this.saveViewArray.find(
      (item) => item._id === this.selectedView
    );
    if (currentViewFilter) {
      if (currentViewFilter.header_filter) {
        currentViewFilter.header_filter.forEach((col: any) => {
          if (this.pinnedColumns.includes(col.colId)) {
             col.hide = false;
             col.pinned = 'left';
          }
        });
      }

      this.skipEventHandler = true;
      this.gridApi.applyColumnState({
        state: currentViewFilter.header_filter,
        applyOrder: true,
      });
      this.gridApi.setFilterModel(currentViewFilter.column_filter);
      this.skipEventHandler = false;
      this.isModified = false;
    }
  }

  get isDuplicateName(): boolean {
    if (!this.viewFilterInput || !this.viewFilterInput.trim()) return false;
    return this.saveViewArray.some(i => i.name.toLowerCase() === this.viewFilterInput.trim().toLowerCase());
  }

  async saveViewFilter() {
    if (!this.viewFilterInput.trim() || this.isDuplicateName) return;
    const checkExistingName = this.saveViewArray.find(i => i.name === this.viewFilterInput);

    if (!checkExistingName) {
      this.isSaving = true;
      try {
        const payload = {
          name: this.viewFilterInput,
          column_filter: this.gridApi.getFilterModel(),
          header_filter: this.gridApi.getColumnState(),
          type: this.viewFilterType,
        };

        let res = await this.savedSearchService.saveViewFilter(payload);
        if (res.status != 'success') return;
        
        await this.updateColumnState(this.gridApi.getFilterModel(), this.gridApi.getColumnState());
        await this.fetchViewFilterList(true);
        this.isModified = false;
        this.viewFilterInput = '';
      } catch (e) {
        console.error("Save view failed", e);
      } finally {
        this.isSaving = false;
      }
    } else {
      const modalRef = this._modalService.open(InfoDialogComponent);
      modalRef.componentInstance.headerTitle = 'Duplicate name';
      modalRef.componentInstance.headerIconStyle = 'text-warning me-2';
      modalRef.componentInstance.headerIcon = 'bi bi-info';
      modalRef.componentInstance.bodyText = 'The name already exists.'
      modalRef.componentInstance.btnStyle = 'btn-info text-white';
    }
  }

  confirmDialog() {
    const currentViewFilter = this.saveViewArray.find(
      (item) => item._id === this.selectedView
    );
    const modalRef = this._modalService.open(ConfirmDialogComponent);

    if (currentViewFilter?.name === "Default Basic") {
      modalRef.componentInstance.headerTitle = 'Reset Default Basic';
      modalRef.componentInstance.headerIconStyle = 'text-danger me-2';
      modalRef.componentInstance.headerIcon = 'bi bi-bootstrap-reboot';
      modalRef.componentInstance.whatToDelete = 'Default Basic';
      modalRef.componentInstance.subtitleText = 'Are you sure you want to <strong class="text-danger">revert the changes</strong> made on ';
      modalRef.componentInstance.btnStyle = 'btn-danger text-white';
    } else {
      modalRef.componentInstance.headerTitle = 'Remove Saved View';
      modalRef.componentInstance.headerIconStyle = 'text-danger me-2';
      modalRef.componentInstance.headerIcon = 'bi bi-trash';
      modalRef.componentInstance.whatToDelete = currentViewFilter?.name;
      modalRef.componentInstance.btnStyle = 'btn-danger text-white';
    }

    modalRef.result.then((result) => {
      if (result === 'confirm') {
        this.deleteViewFilter();
      }
    }).catch((error) => {
      console.error(error);
    });
  }

  async deleteViewFilter() {
    if (!this.selectedView) return;
    let res = await this.savedSearchService.deleteViewFilter(this.selectedView, {type: this.viewFilterType});
    this.selectedView = null;

    if (res.status != 'success') return;
    this.skipEventHandler = true;
    await this.fetchViewFilterList(false);
  }

  async clearFilter() {
    if (!this.gridApi) return;
    
    const currentViewFilter = this.saveViewArray.find(
      (item) => item.status === true
    );

    if (currentViewFilter) {
      const payload = {
        name: currentViewFilter?.name,
        status: false,
        type: this.viewFilterType,
      };
      this.savedSearchService.updateViewFilter(currentViewFilter?._id, payload);
    }

    this.selectedView = null;
    this.saveViewArray.forEach(v => v.status = false);
    this.showViewFilterSelectInput = true;
    this.skipEventHandler = true;
    this.showAddViewFilterButton = false;
    this.showClearFilter = false;
    this.showViewFilterRemoveButton = false;

    this.gridApi.resetColumnState();
    this.gridApi.setFilterModel(null);
    this.viewFilterInput = '';

    await this.updateColumnState(this.gridApi.getFilterModel(), this.gridApi.getColumnState());
    
    this.onClearFilters.emit();
    this.skipEventHandler = false;
    this.isModified = false;
  }

  handleGridEvent(event: any) {
    if (this.skipEventHandler) return;

    if (
      event.type == 'dragStopped' || 
      event.type == 'filterChanged' || 
      event.type == 'columnVisible' || 
      event.type == 'sortChanged' ||
      event.type == 'columnResized'
    ) {
      this.showActionViewSearchButton = true;
      this.saveColumnState();
      
      this.showClearFilterValidation();
    }
  }

  onColumnVisible(event: any) {
    this.handleGridEvent(event);
  }
}
